import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnInit, Renderer2 } from '@angular/core';
import { AuthenticationService } from './core/authentication/service/authentication.service';
import { EventBusService } from "./share/services/event-bus-service/event-bus.service";
import { Util } from "./share/util/util";
import { CATALOG_TAB, DEBOUNCE_TIME, DOWNLOAD_TAB, TABLET_BREAKPOINT, WEBSITE_TAB } from "./share/constant/variable-constant";
import { MobileFilter } from './share/model/mobileFilter.model';
import {
    IFilterChangeEvent,
    IFilterDateRangeOptionChangeEvent,
    IFilterDropdownChangeEvent,
    IFilterGroup,
    ISiSChangeEvent
} from 'src/assets/vendor/customer-experience-browser-client/dist/types/components/dc-filter/filter-types';
import { SisCacheService } from './share/cache/search-in-search/sis-cache.service';
import { DateFormatCacheService } from './share/cache/date-format-cache.service';
import { TranslateService } from '@ngx-translate/core';
import { BLANK } from './share/constant/special-characters-constant';
import { DetectScreenSizesService } from './share/services/detect-screen-sizes-service/detect-screen-sizes.service';
import { Subscription, fromEvent } from 'rxjs';
import { distinctUntilChanged, throttleTime } from 'rxjs/operators';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AppComponent implements OnInit {
  currentTab?: string;
  mobileDisplayFilters: boolean = false;
  mobileFilters?: MobileFilter;
  tabletBreakPoint: string = TABLET_BREAKPOINT;
  shareScreenSize$;
  private subscription?: Subscription;

  constructor(
    private ngZone: NgZone,
    private renderer: Renderer2,
    private cdf: ChangeDetectorRef,
    private sisCache: SisCacheService,
    private authenService: AuthenticationService,
    private eventBusService: EventBusService,
    private translateService: TranslateService,
    private util: Util,
    private elementRef: ElementRef,
    private dateFormatCacheInit: DateFormatCacheService,
    private detectScreenSizesService: DetectScreenSizesService,
  ) {
    this.shareScreenSize$ = this.detectScreenSizesService.shareScreenSize$;
  }

  ngOnInit() {
    this.authenService.init();
    this.dateFormatCacheInit.init();
    this.detectScreenSizesService.init();
    this.initEventPropagation();
    this.registerResizeFn();
    this.registerWindowScroll();
  }

  /**
   * Emit events to the outside world - @Output w/ EventEmitter is not able to do so...
   * @param name
   * @param detail
   * @private
   */
  private emit(name: string, detail: unknown): void {
    (this.elementRef.nativeElement as HTMLElement).dispatchEvent(new CustomEvent(name, { detail }));
  }

  private emitAddToCart(detail: { query: string, resultTitle: string }) {
    this.emit('searchAddToCart', detail);
  }

  private emitEngagementDownload(
    detail: {
      documentId: string,
      documentName: string,
      documentType: string,
      documentExtension: string,
      linkUrl: string,
      linkClasses: string[],
      linkText: string,
      linkId?: string,
      searchTerm: string,
      searchPosition: number,
      searchPage: number,
      searchPerPage: number,
      searchFilters: string,
      searchSortby: string,
    }) {
    this.emit('searchEngagementDownload', detail);
  }

  private emitFilter(detail: { filter: string, query: string, filterState: boolean, tab: string }) {
    this.emit('searchFilter', detail);
  }

  private emitPerPage(detail: { count: number, query: string, tab: string }) {
    this.emit('searchPerPage', detail);
  }

  private emitResultLink(detail: { query: string, resultTitle: string, resultUrl: string, tab: string }) {
    this.emit('searchResultLink', detail);
  }

  private emitResult(detail: { query: string, resultCount: number, tab: string }) {
    this.emit('searchResults', detail);
  }

  private emitSortBy(detail: { query: string, sortBy: string, tab: string }) {
    this.emit('searchSortBy', detail);
  }

  private emitTab(detail: { query: string, tab: string }) {
    this.emit('searchTab', detail);
  }

  private emitEngagementShare(detail: {
    fileExtension: string,
    fileName: string,
    fileId: string,
    shareType: string,
    linkUrl: string,
    linkClasses?: string[],
    linkText: string,
    linkId?: string,
    searchTerm: string,
    searchPosition: number,
    searchPage: number,
    searchPerPage: number,
    searchFilters: string,
    searchSortby: string,
  }) {
    this.emit('searchEngagementShare', detail);
  }

  private emitEngagementNotify(detail: {
    fileExtension: string,
    fileName: string,
    fileId: string,
    fileSubscribe: number;
    linkUrl: string,
    linkClasses: string[],
    linkId?: string,
    linkText: string,
    searchTerm: string,
    searchPosition: number,
    searchPage: number,
    searchPerPage: number,
    searchFilters: string,
    searchSortby: string,
  }) {
    this.emit('searchEngagementNotify', detail);
  }

  private emitEngagementDocumentResult(detail: {
    fileId?: string,
    linkUrl: string,
    linkText: string,
    searchTerm: string,
    searchPosition: number,
    searchPage: number,
    searchPerPage: number,
    searchFilters: string,
    searchSortby: string,
  }) {
    this.emit('searchEngagementDocumentResult', detail);
  }

  private emitViewSearchResults(detail: {
    searchTerm: string,
    searchInSearch: string,
    searchOrigin: string,
    searchCatalogCount: number,
    searchDownloadCount: number,
    searchWebsiteCount: number,
    searchActiveTab: string,
    searchSortBy: string,
    searchPage: number,
    searchPerPage: number,
    filters?: Array<IFilterGroup>,
  }) {
    this.emit('viewSearchResults', detail);
  }

  private initEventPropagation() {
    let cCount: number;
    let wCount: number;
    let dCount: number;

    const emitTabEvent = () => {
      if ([cCount, wCount, dCount, this.currentTab].includes(undefined)) {
        return;
      }

      this.emitTab({
        query: this.util.getQuery(),
        tab: this.currentTab!,
      });
    };

    const emitZeroResultsEvent = () => {
      if (cCount + wCount + dCount !== 0) {
        return;
      }

      this.emitTab({
        query: this.util.getQuery(),
        tab: '0_results',
      });
    };

    this.eventBusService.on('changeTab', tabName => {
      this.currentTab = tabName;
      emitTabEvent();
    });

    this.eventBusService.on('cTotal', (resultCount, _, isError) => {
      this.emitResult({
        query: this.util.getQuery(),
        resultCount,
        tab: CATALOG_TAB.selectedTab,
      });

      cCount = resultCount;
      emitZeroResultsEvent();
    });

    this.eventBusService.on('emitEvent', ({name,  detail}) => {
        this.emit(name, detail);
    });

    this.eventBusService.on('wSearchContainer', search => {
      const resultCount = search.result.count;
      this.emitResult({
        query: search.query ?? this.util.getQuery(),
        resultCount,
        tab: WEBSITE_TAB.selectedTab,
      });

      wCount = resultCount;
      emitZeroResultsEvent();
    });

    this.eventBusService.on('searchContainer', search => {
      const resultCount = search.result.count;
      this.emitResult({
        query: search.query ?? this.util.getQuery(),
        resultCount,
        tab: DOWNLOAD_TAB.selectedTab,
      });

      dCount = resultCount;
      emitZeroResultsEvent();
    });

    this.eventBusService.on('filterToggle', ({ tab, filter, state }) => {
      this.emitFilter({
        filter,
        filterState: state,
        query: this.util.getQuery(),
        tab,
      });
    });

    this.eventBusService.on('addToCart', resultTitle => {
      this.emitAddToCart({
        query: this.util.getQuery(),
        resultTitle,
      });
    });

    this.eventBusService.on('changeSortBy', ({ option, tab }) => {
      this.emitSortBy({
        query: this.util.getQuery(),
        sortBy: option,
        tab,
      });
    });

    this.eventBusService.on('searchResultLinkClick', ({ label, tab, url }) => {
      this.emitResultLink({
        query: this.util.getQuery(),
        resultTitle: label,
        resultUrl: url,
        tab,
      });
    });

    this.eventBusService.on('searchEngagementDownload', (detail): void => {
      this.emitEngagementDownload(detail);
    });

    this.eventBusService.on('changePagination', ({ count, tab }) => {
      this.emitPerPage({
        query: this.util.getQuery(),
        count,
        tab,
      });
    });

    this.eventBusService.on('displayMobileFilter', (filter: MobileFilter) => {
      this.mobileFilters = filter;
      this.mobileDisplayFilters = true;
      this.cdf.markForCheck();
    });

    this.eventBusService.on('searchEngagementShare', (detail) => {
      this.emitEngagementShare(detail);
    });

    this.eventBusService.on('searchEngagementNotify', (detail): void => {
      this.emitEngagementNotify(detail)
    })

    this.eventBusService.on('searchEngagementDocumentResult', (detail): void => {
      this.emitEngagementDocumentResult(detail);
    })

    this.eventBusService.on('viewSearchResults', ({ searchTerm, searchInSearch, searchOrigin, searchCatalogCount, searchDownloadCount, searchWebsiteCount, searchActiveTab, searchSortBy, searchPage, searchPerPage, filters, }) => {
      this.emitViewSearchResults({
        searchTerm,
        searchInSearch,
        searchOrigin,
        searchCatalogCount,
        searchDownloadCount,
        searchWebsiteCount,
        searchActiveTab,
        searchSortBy,
        searchPage,
        searchPerPage,
        filters,
      })
    })

  }

  @HostListener('searchInSearchChange', ['$event.detail'])
  onChangeDCSiS(evt: ISiSChangeEvent) {

    if (!this.mobileDisplayFilters) return;

    this.eventBusService.emit('sisFromMobile', evt);
    this.mobileFilters = undefined;
    this.mobileDisplayFilters = false;
  }

  @HostListener('searchChange', ['$event.detail'])
  onChangeDCSearchInResult(changedValue: string) {

    if (!this.mobileDisplayFilters) return;

    const text = changedValue.trim();
    if (text.length > 0) {
      const sisCache = this.sisCache.dcFilterSearch$.getValue();

      const isSearchWithInExists = sisCache.tagClouds.some((val: string) => val.toLowerCase() == text.toLowerCase());
      if (isSearchWithInExists) {
        const sisCacheCopyForExistingKeyword = { ...sisCache };
        sisCacheCopyForExistingKeyword.value = text;
        this.sisCache.add(sisCacheCopyForExistingKeyword);
        setTimeout(() => {
          window.dcApp.services.Toast(`'${text}' ${this.translateService.instant('dc.myrn.toast.duplicated_keyword')}`);
        }, DEBOUNCE_TIME - 200);
        return;
      }

      const sisCacheCopyForNewKeyword = { ...sisCache };
      sisCacheCopyForNewKeyword.value = BLANK;
      this.sisCache.add(sisCacheCopyForNewKeyword);

      this.eventBusService.emit('sisPerformFromMobile', changedValue);
      this.mobileFilters = undefined;
      this.mobileDisplayFilters = false;
    }
  }

  @HostListener('filterChange', ['$event.detail'])
  onChangeDCFilter(evt: IFilterChangeEvent) {

    if (!this.mobileDisplayFilters) return;

    this.eventBusService.emit('filterFromMobile', evt);
    this.mobileFilters = undefined;
    this.mobileDisplayFilters = false;
  }

  @HostListener('filterAllClearPressed', ['$event.detail'])
  onFilterAllClearPressed() {

    if (!this.mobileDisplayFilters) return;

    this.eventBusService.emit('clearAllFiltersFromMobile');
    this.mobileDisplayFilters = false;
    this.mobileFilters = undefined;
  }

  @HostListener('dropdownSelected', ['$event.detail'])
  onContentTypeDropdownSelected(evt: IFilterDropdownChangeEvent) {

    if (!this.mobileDisplayFilters) return;

    this.eventBusService.emit('mobileDropdownSelected', evt);
    this.mobileFilters = undefined;
    this.mobileDisplayFilters = false;
  }

  @HostListener('dateRangeOptionFilterApplied', ['$event.detail'])
  onApplyDateFiltersClick(evt: IFilterDateRangeOptionChangeEvent) {

    if (!this.mobileDisplayFilters) return;

    this.eventBusService.emit('mobileDateRangeOptionFilterApplied', evt);
    this.mobileFilters = undefined;
    this.mobileDisplayFilters = false;
  }

  private executeResizeFn(event: Event) {
    event.preventDefault();
    if (!window.dcApp.services.Breakpoint._hasBreakPointChanged()) return;
    this.detectScreenSizesService.add(window.dcApp.services.Breakpoint.getCurrentBreakpoint());
  }

  private registerResizeFn() {
    this.ngZone.runOutsideAngular(() => {
      this.renderer.listen('window', 'resize', (event: Event) => {
        this.ngZone.run(() => this.executeResizeFn(event));
      });
    });
  }

  private executeWindowScroll(event: Event) {
    event.preventDefault();
    this.eventBusService.emit('scrollWindow', event);
  }

  private registerWindowScroll() {
    this.ngZone.runOutsideAngular(() => {
      if (this.detectScreenSizesService.isMobile(TABLET_BREAKPOINT)) {
        const scrollSubscription = fromEvent(window, 'scroll')
          .pipe(distinctUntilChanged(), throttleTime(100))
          .subscribe((event: Event) => {
            this.ngZone.run(() => this.executeWindowScroll(event));
          });
        this.subscription?.add(scrollSubscription);
      } else {
        this.subscription?.unsubscribe();
      }
    });
  }

}
